// apps/web/src/widgets/ranked-tier/renderer.tsx
//
// Phase 7 — WIDG-07 RankedTierRenderer (D-05..D-09 typed config). N-tier
// display with recipe-declared color trios + data-tier-rank + data-tier-id
// structural mitigation per PITFALLS §N3 (data-driven CSS bridge — NO named
// .tier-best/.tier-good/.tier-ok/.tier-bad classes; grep gate enforces). // grep-allow: tier-best, tier-good, tier-ok, tier-bad
//
// State shape: undefined (read-only display; reads source option-set state
// via useOtherWidgetState per D-07 composition). The renderer composes (not
// nests) over the option-set widget — config.optionSetWidgetId is the FK
// across widget instances in the same recipe; the source widget supplies
// options[] + per-option tierId assignments; this renderer supplies tier
// metadata + bucketing + color rendering.
//
// UI-SPEC §"Ranked-Tier Visual Contract" lines 446-604: emits ONE inline
// style attribute on the renderer root (the SOLE permitted inline style in
// Phase 7 per UI-SPEC line 805) for per-tier CSS custom properties; tier-
// swatch classes consume them via Branch B static rank-based classes
// (`tier-rank-${rank}-bg` etc.) declared in apps/web/src/styles/globals.css.
//
// Branch decision (Task 0 probe 2026-06-09): BRANCH B. Tailwind v4.3.0's
// @source inline() supports brace-expansion only, not glob — so id-based
// arbitrary-value classes (Branch A) cannot be generated at build time
// because tier ids are author-declared. Branch B uses rank-based static
// classes (36 in globals.css) with D-06 N <= 12 cap bounding the universe.
//
// PITFALLS §M5/§N3 forward-mitigation: schema-side data-driven tiers (Plan 03)
// + renderer-side data-driven CSS (this file) + grep gate (Plan 01) form a
// three-layer defense against the closed-enum 4-tier overfit.
//
// Sanitization: tier colors are recipe content; Pass 2A schema validation
// (Plan 03 RankedTierConfigSchema) enforces non-empty length-bounded color
// strings BEFORE the recipe reaches this renderer. Helmet CSP `style-src
// 'self' 'unsafe-inline'` (Phase 6 contingency) covers the inline style
// attribute. The browser CSS parser rejects non-color tokens at apply time
// per threat-model T-07-09-02 disposition.

import type { OptionSetConfig, RankedTier } from "@the-recipe-spread/shared";
import { Card, CardContent } from "@/components/ui/card";
import { useOtherWidgetState } from "../use-other-widget-state.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import { buildTierCssVars, sortTiersByRank } from "./tier-css.js";

// Source-option-set lookup: returns the OptionSetConfig.options array, or
// null when the source widget is missing OR is the wrong template id OR has
// a malformed config (missing/non-array options field). Pass 2A guarantees
// the shape in fully-validated recipes, but the renderer must degrade
// gracefully for partially-typed callers — the Phase 8 authoring preview
// surface drives this renderer with in-progress recipes where Pass 2A may
// not have run yet, and Phase 11 AI extraction can produce option-set widgets
// with partial configs. Returning null routes those through the "source
// missing" fallback branch instead of crashing the spread render on a
// non-iterable `undefined`.
function readSourceOptions(
  recipeWidgets: ReadonlyArray<{ instanceId: string; templateId: string; config: unknown }>,
  optionSetWidgetId: string,
): OptionSetConfig["options"] | null {
  const sourceWidget = recipeWidgets.find((w) => w.instanceId === optionSetWidgetId);
  if (sourceWidget === undefined) return null;
  if (sourceWidget.templateId !== "option-set") return null;
  // Pass 2A validates this shape; the cast here is the documented seam.
  // Defense-in-depth: structural check on `options` BEFORE returning so a
  // partial-typed config (e.g., `{ cardinality: { min: 0 } }` with no
  // `options` field) routes to the null-fallback branch instead of returning
  // undefined and crashing the downstream `for (const opt of sourceOptions)`
  // iteration.
  const cfg = sourceWidget.config as OptionSetConfig;
  if (!Array.isArray(cfg.options)) return null;
  return cfg.options;
}

export function RankedTierRenderer({
  widget,
  config,
  recipe,
}: WidgetRendererProps<"ranked-tier">): React.ReactElement {
  // D-07 composition: read source option-set state via the engine hook.
  const sourceState = useOtherWidgetState(config.optionSetWidgetId);
  const selectedIds: ReadonlyArray<string> =
    sourceState !== undefined && "selected" in sourceState ? sourceState.selected : [];

  // Source-widget lookup from recipe.widgets[]. The two reads (source widget
  // via recipe.widgets + source state via useOtherWidgetState) are decoupled
  // by design: state lives in the provider (mutable), options live in the
  // recipe (immutable post-validation).
  const sourceOptions = readSourceOptions(recipe.widgets ?? [], config.optionSetWidgetId);

  const sortedTiers = sortTiersByRank(config.tiers);
  const tierCssVars = buildTierCssVars(config.tiers);
  const labelId = `${widget.instanceId}-label`;

  // Empty-tiers branch (UI-SPEC line 266) — render the Card shell + fallback,
  // skip the entire bucketing pipeline.
  if (config.tiers.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <section aria-labelledby={labelId} className="flex flex-col gap-2">
            <h3
              id={labelId}
              className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
            >
              {widget.name}
            </h3>
            <p className="text-sm text-[var(--recipe-muted)]">No tiers declared.</p>
          </section>
        </CardContent>
      </Card>
    );
  }

  // Missing-source-widget branch — render Card shell + fallback, skip
  // bucketing (no options to bucket).
  if (sourceOptions === null) {
    return (
      <Card style={tierCssVars}>
        <CardContent className="p-4">
          <section aria-labelledby={labelId} className="flex flex-col gap-2">
            <h3
              id={labelId}
              className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
            >
              {widget.name}
            </h3>
            <p className="text-sm text-[var(--recipe-muted)]">Source option-set widget missing.</p>
          </section>
        </CardContent>
      </Card>
    );
  }

  // Bucketing pass — for each tier, collect source options whose tierId
  // matches. Untiered options (no tierId field) go into a separate bucket
  // that's suppressed when empty (W-4 negative test).
  type SourceOption = OptionSetConfig["options"][number];
  const tierBuckets = new Map<string, SourceOption[]>();
  for (const tier of sortedTiers) {
    tierBuckets.set(tier.id, []);
  }
  const untieredOptions: SourceOption[] = [];
  for (const opt of sourceOptions) {
    if (opt.tierId === undefined) {
      untieredOptions.push(opt);
      continue;
    }
    const bucket = tierBuckets.get(opt.tierId);
    // If the option's tierId references a tier NOT in this widget's
    // config.tiers (recipe-author mistake), the option silently drops out of
    // both the per-tier bucket AND the Untiered bucket. This is intentional:
    // surfacing it visually would require either rendering a misconfigured
    // bucket or a UI error state; UI-SPEC defers either choice. Pass 2A is
    // the canonical place to surface this at validation time.
    if (bucket !== undefined) bucket.push(opt);
  }

  return (
    // THE ONE permitted inline style in Phase 7 — UI-SPEC line 805. The
    // CSS custom properties cascade to the swatch className via globals.css
    // static classes (Branch B rank-based).
    <Card style={tierCssVars}>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-2">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {widget.name}
          </h3>
          <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-2">
            {sortedTiers.map((tier: RankedTier) => {
              const bucket = tierBuckets.get(tier.id) ?? [];
              // String-concat className per PITFALLS §1 + RESEARCH §Pattern 3
              // line 537 — template-literal interpolation defeats the JIT
              // scanner; here the static class names are pre-declared in
              // globals.css (Branch B) but the concat form keeps the literal
              // substring "tier-rank-" visible to the scanner for consistency
              // with Branch A semantics.
              const swatchClass =
                "rounded-md border p-3 text-sm flex flex-col gap-2 " +
                "tier-rank-" +
                tier.rank +
                "-bg " +
                "tier-rank-" +
                tier.rank +
                "-border " +
                "tier-rank-" +
                tier.rank +
                "-fg";
              return (
                <div
                  key={tier.id}
                  data-tier-rank={tier.rank}
                  data-tier-id={tier.id}
                  className={swatchClass}
                >
                  <span className="text-xs font-semibold uppercase tracking-wider leading-5">
                    {tier.label}
                  </span>
                  {bucket.length === 0 ? (
                    <span className="text-xs italic">(no options in this tier)</span>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {bucket.map((opt) => (
                        <li
                          key={opt.id}
                          className={`text-sm ${selectedIds.includes(opt.id) ? "font-semibold" : ""}`}
                        >
                          {opt.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
          {/* W-4: Untiered bucket suppressed when zero untiered options. */}
          {untieredOptions.length > 0 ? (
            <div className="mt-2 rounded-md border bg-[var(--recipe-card)] border-[var(--recipe-line)] p-3 flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider leading-5 text-[var(--recipe-muted)]">
                Untiered
              </span>
              <ul className="flex flex-col gap-1">
                {untieredOptions.map((opt) => (
                  <li
                    key={opt.id}
                    className={`text-sm ${selectedIds.includes(opt.id) ? "font-semibold" : ""}`}
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      </CardContent>
    </Card>
  );
}
